import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import qicardPayments from "../services/qicardPayments";
import invoiceService from "../services/InvoiceService";
import { useCart } from "../contexts/CartContext";
import { loadQiCardPending, clearQiCardPending } from "./qicardStorage";
import { emitCheckoutUiEvent } from "./checkoutEvents";
import { loadCapturedAmount, loadHeldCheckoutOrder, clearHeldCheckoutOrder } from "./checkoutSession";
import { isQiCardTerminalStatus, mapQiStatusToUi } from "./qicardStatus";
import { tryResolvePaidCheckout } from "./checkoutPaymentResolve";

/**
 * If the customer returns to the app with a parked QiCard/card txn,
 * poll once and announce — unless the dedicated return route owns it.
 */
const CheckoutResume: React.FC = () => {
  const location = useLocation();
  const { clearCart } = useCart();

  useEffect(() => {
    if (location.pathname.startsWith("/payment/return")) return;
    if (location.pathname.startsWith("/payment-callback")) return;

    const parked = loadQiCardPending();
    if (!parked?.transactionId) return;

    let cancelled = false;
    const run = async () => {
      try {
        const { status } = await qicardPayments.getStatus(parked.transactionId);
        if (cancelled) return;
        if (!isQiCardTerminalStatus(status)) return;
        const ui = mapQiStatusToUi(status);
        if (ui === "success") {
          const held = loadHeldCheckoutOrder();
          if (held?.orderId && parked.orderId && held.orderId === parked.orderId) {
            clearCart();
            clearHeldCheckoutOrder();
          }
          clearQiCardPending();
          emitCheckoutUiEvent({
            type: "success",
            amount: parked.amount ?? loadCapturedAmount(),
            orderId: parked.orderId,
            landOn: "step1",
          });
        }
      } catch {
        /* leave parked */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, clearCart]);

  useEffect(() => {
    if (location.search.includes("paymentStatus")) return;
    if (location.pathname.startsWith("/payment-callback")) return;
    const cardId = localStorage.getItem("lastCardPaymentTransactionId");
    if (!cardId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await invoiceService.getStatusDetailsData("CardPayment", cardId);
        if (cancelled) return;
        const status = String(
          (res.data as { status?: string })?.status ?? res.data ?? ""
        ).toUpperCase();
        const hyperPayOk =
          status.includes("SUCCESS") || status.startsWith("000.");
        let paid = hyperPayOk;
        if (!paid) {
          const resolved = await tryResolvePaidCheckout(cardId);
          paid = resolved.paid;
        }
        if (paid) {
          localStorage.removeItem("lastCardPaymentTransactionId");
          const held = loadHeldCheckoutOrder();
          if (held?.orderId) {
            clearCart();
            clearHeldCheckoutOrder();
            emitCheckoutUiEvent({
              type: "success",
              amount: loadCapturedAmount(),
              orderId: held.orderId,
              landOn: "step1",
            });
          }
        }
      } catch {
        /* ignore */
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, clearCart]);

  return null;
};

export default CheckoutResume;
